import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { FaArrowLeft, FaCalendarAlt, FaMinus, FaPlus } from "react-icons/fa";
import { IoIosArrowDown, IoIosCheckmarkCircleOutline } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../../Utils/apiFetch";
import apiPath from "../../Utils/apiPath";
import { debounce, isEmpty, startCase, upperCase } from "lodash";
import InternalLoader from "../../component/InternalLoader";
import { toFixedWithoutRounding } from "../../Utils/Toast";
import AuthContext from "../../context/AuthContext";
import { useContextSelector } from "use-context-selector";
import CalendarWeb from "./CalendarWeb";
import ReferenceProfitLoss from "./ReferenceProfitLoss";
const ProfitLoss = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [data, setData] = useState([]);
  const [active, setActive] = useState({});
  const [showCalendar, setShowCalendar] = useState(false);
  const [show, setShow] = useState({});
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    fromPeriod: moment().format("YYYY-MM-DD HH:mm:ss"),
    toPeriod: moment().format("YYYY-MM-DD HH:mm:ss"),
    betType: "BetFair",
    status: "completed",
    tab: embedded ? "All" : "sports",
  });
  const profileData = useContextSelector(AuthContext, (ctx) => ctx.profileData);

  const from = moment(filter?.fromPeriod);
  const to = moment(filter?.toPeriod);

  // 1️⃣ Check if both are today
  const isToday = from.isSame(moment(), "day") && to.isSame(moment(), "day");

  // 2️⃣ Check if range is last 7 days (including today)
  const isLast7Days =
    from.isSame(moment().subtract(6, "days"), "day") &&
    to.isSame(moment(), "day");

  // 3️⃣ Check if both are yesterday
  const isYesterday =
    from.isSame(moment().subtract(1, "day"), "day") &&
    to.isSame(moment().subtract(1, "day"), "day");
  const format = "YYYY-MM-DD HH:mm:ss";

  const setToday = () => {
    return {
      fromPeriod: moment().startOf("day").format(format),
      toPeriod: moment().endOf("day").format(format),
    };
  };

  const setYesterday = () => {
    return {
      fromPeriod: moment().subtract(1, "day").startOf("day").format(format),
      toPeriod: moment().subtract(1, "day").endOf("day").format(format),
    };
  };

  const setLast7Days = () => {
    return {
      fromPeriod: moment().subtract(6, "days").startOf("day").format(format),
      toPeriod: moment().endOf("day").format(format),
    };
  };
  let dummy = {
    BetFair: "betfair",
    Bookmaker: "bookmaker",
    Fancy: "fancy",
    SportsBook: "sportBook",
  };
  const getData = async (obj = filter) => {
    try {
    if (obj.tab === "All") {
      const types = ["casino", "betfair", "bookmaker", "fancy", "sportBook"];
      const responses = await Promise.all(types.map(betType => apiGet(apiPath.profitLoss, {
        betType, status: "completed",
        fromPeriod: moment(obj.fromPeriod).format("YYYY-MM-DD"),
        toPeriod: moment(obj.toPeriod).format("YYYY-MM-DD"),
        page: obj.page || 1, limit: obj.limit,
      })));
      setData(responses.flatMap((response, index) => {
        if (response.status !== 200 || !response.data?.success) return [];
        const records = response.data.results?.data || [];
        return index === 0 ? records : records.filter(row => row.bets_list?.length).map(row => ({ ...row, total: calculateTotal(row, types[index]) }));
      }));
      return;
    }
    const { status, data } = await apiGet(apiPath.profitLoss, {
      betType: obj?.tab == "Casino" ? "casino" : dummy[obj?.betType],
      status: "completed",
      fromPeriod: moment(obj?.fromPeriod).format("YYYY-MM-DD"),
      toPeriod: moment(obj?.toPeriod).format("YYYY-MM-DD"),
      page: obj?.page || 1,
      limit: obj?.limit,
    });
    if (status == 200) {
      if (data?.success) {
        setData(
          obj?.tab == "Casino"
            ? (data?.results?.data || [])
            : (data?.results?.data || [])
                .filter((res) => {
                  return res?.bets_list?.length > 0;
                })
                ?.map((res) => {
                  return {
                    ...res,
                    total: calculateTotal(res, dummy[filter?.betType]),
                  };
                })
        );
      }
    }
    } catch (error) {
      setData([]);
    } finally {
      setLoader(false);
    }
  };

  const calculateTotal = (markets, newTab) => {
    if (!Array.isArray(markets?.bets_list) || markets?.bets_list.length === 0)
      return 0;

    const commissionRate = parseFloat(profileData?.commission) || 0;

    let backProfit = 0,
      backLoss = 0,
      layProfit = 0,
      layLoss = 0,
      yesProfit = 0,
      yesLoss = 0,
      noProfit = 0,
      noLoss = 0,
      totalBet = 0;
    // ✅ Process each bet & add updatedResult
    markets?.bets_list?.forEach((bet) => {
      const profit = parseFloat(bet.profitAmount) || 0;
      const loss = parseFloat(bet.loseAmount) || 0;
      totalBet += parseFloat(bet?.amount) || 0;
      let updateResultValue = 0;

      // 🎯 Back / Lay Bets (Exchange)
      if (bet?.betType === "back") {
        if (bet?.teamSelectionWin === bet?.selectionId) {
          backProfit += profit;
          updateResultValue = profit; // Win
        } else {
          backLoss += loss;
          updateResultValue = -loss; // Loss
        }
      } else if (bet?.betType === "lay") {
        if (bet?.teamSelectionWin !== bet?.selectionId) {
          layProfit += profit;
          updateResultValue = profit; // Win
        } else {
          layLoss += loss;
          updateResultValue = -loss; // Loss
        }
      }

      // 🎯 Fancy Yes / No Bets
      if (bet?.type === "Yes") {
        if (bet?.decisionRun >= bet?.betRun) {
          yesProfit += profit;
          updateResultValue = profit; // Win
        } else {
          yesLoss += loss;
          updateResultValue = -loss; // Loss
        }
      } else if (bet?.type === "No") {
        if (bet?.decisionRun < bet?.betRun) {
          noProfit += profit;
          updateResultValue = profit; // Win
        } else {
          noLoss += loss;
          updateResultValue = -loss; // Loss
        }
      }

      // ✅ Add single key for result
      return {
        ...bet,
        updatedResult: updateResultValue,
      };
    });

    // 🎯 Calculate subtotals
    const backSubTotal = backProfit - backLoss;
    const laySubTotal = layProfit - layLoss;
    const marketSubTotal = backSubTotal + laySubTotal;

    const yesSubTotal = yesProfit - yesLoss;
    const noSubTotal = noProfit - noLoss;
    const fancySubTotal = yesSubTotal + noSubTotal;

    // 🎯 Calculate Net Amount based on Tab
    let netAmount = 0;
    if (newTab === "toss" || newTab === "tie") {
      netAmount = marketSubTotal;
    } else if (newTab === "fancy") {
      netAmount = fancySubTotal;
    } else {
      const commission =
        marketSubTotal > 0 ? (marketSubTotal * commissionRate) / 100 : 0;
      netAmount = marketSubTotal - commission;
    }

    return { pl: netAmount, totalBet: totalBet };
  };

  const debouncedFetch = useCallback(
    debounce((obj) => {
      getData(obj);
    }, 500), // wait 600ms
    []
  );
  useEffect(() => {
    setLoader(true);
    debouncedFetch(filter);
  }, [filter]);
  let obj = {
    4: "Cricket",
    2: "Tennis",
    1: "Soccer",
    3: "Casino",
  };
  const ShowPrice = (res) => {
    if (isEmpty(res?.fancyName)) {
      if (
        (res?.teamSelectionWin == res?.selectionId && res?.betType == "back") ||
        (res?.teamSelectionWin != res?.selectionId && res?.betType == "lay")
      ) {
        return res?.profitAmount;
      } else {
        return -res?.loseAmount;
      }
    } else {
      if (res?.type == "No") {
        if (res?.decisionRun < res?.betRun) {
          return res?.profitAmount;
        } else {
          return -res?.loseAmount;
        }
      } else {
        if (res?.decisionRun >= res?.betRun) {
          return res?.profitAmount;
        } else {
          return -res?.loseAmount;
        }
      }
    }
  };

  if (embedded) return <ReferenceProfitLoss data={data} filter={filter} setFilter={setFilter} loading={loader} onSearch={() => { setLoader(true); getData(filter); }} />;

  return (
    <div id="mc_container" className="personal-loss EN">
      <div className="tab-nav profit-tab">
        <div
          onClick={() => {
            setData([]);
            setFilter((prev) => {
              return { ...prev, tab: "Casino" };
            });
          }}
          className={`nav-item ${filter?.tab == "Casino" ? "active" : ""}`}
        >
          Casino
        </div>
        <div
          onClick={() => {
            setData([]);
            setFilter((prev) => {
              return { ...prev, tab: "sports" };
            });
          }}
          className={`nav-item ${filter?.tab == "sports" ? "active" : ""}`}
        >
          Sport
        </div>
      </div>
      <div className="search-form" id="ui-id-6">
        <div
          onClick={() =>
            setFilter((prev) => {
              return {
                ...prev,
                fromPeriod: setToday()?.fromPeriod,
                toPeriod: setToday()?.toPeriod,
              };
            })
          }
          className="form-group "
        >
          <input
            id="today_record"
            type="radio"
            defaultChecked={isToday ? "checked" : ""}
            className="radio-control"
            name="set_date"
          />
          <label htmlFor="today_record" className="radio-control-ie" />
          <label htmlFor="today_record" translate="mc_today">
            Today
          </label>
        </div>
        <div
          onClick={() =>
            setFilter((prev) => {
              return {
                ...prev,
                fromPeriod: setYesterday()?.fromPeriod,
                toPeriod: setYesterday()?.toPeriod,
              };
            })
          }
          className="form-group"
        >
          <input
            id="yestaday_record"
            type="radio"
            defaultChecked={isYesterday ? "checked" : ""}
            className="radio-control"
            name="set_date"
          />
          <label htmlFor="yestaday_record" className="radio-control-ie" />
          <label htmlFor="yestaday_record">Yesterday</label>
        </div>
        <div
          onClick={() =>
            setFilter((prev) => {
              return {
                ...prev,
                fromPeriod: setLast7Days()?.fromPeriod,
                toPeriod: setLast7Days()?.toPeriod,
              };
            })
          }
          className="form-group"
          style={{ display: "false" }}
        >
          <input
            type="radio"
            defaultChecked={isLast7Days ? "checked" : ""}
            id="last_record"
            className="radio-control"
            name="set_date"
          />
          <label htmlFor="last_record" className="radio-control-ie" />
          <label htmlFor="last_record" translate>
            7-Days
          </label>
        </div>
        <div onClick={() => setShowCalendar(true)} className="form-group">
          <input
            id="datetime_range_picker"
            type="radio"
            checked={!isToday && !isYesterday && !isLast7Days ? true : false}
            defaultValue="custom"
            className="radio-control"
            name="set_date"
          />
          <label
            htmlFor="datetime_range_picker"
            className="radio-control-ie"
          ></label>
          <input
            type="text"
            value={`${moment(filter?.fromPeriod).format("MM/DD")} - ${moment(
              filter?.toPeriod
            ).format("MM/DD")}`}
            className="input-control date js_date"
          />
        </div>
        <div className="form-group v-line" />
        <div onClick={() => getData()} className="form-group">
          <button className="button btn-submit" translate="button_search">
            Search
          </button>
        </div>{" "}
        {showCalendar && (
          <CalendarWeb
            fromPeriod={filter?.fromPeriod}
            toPeriod={filter?.toPeriod}
            setFilter={setFilter}
            onCancel={() => setShowCalendar(false)}
          />
        )}
      </div>
      {loader && <div className="mc-loading" />}
      <div className="table-data data-col-6 RNG js_table">
        <div className="table-thead">
          <div className="table-tr">
            <div
              style={{ width: "13%" }}
              className="table-td"
              translate="label_date"
            >
              Date
            </div>
            <span
              style={{ width: "32%" }}
              className="table-td"
              translate="label_deposit"
            >
              Event Name
            </span>
            <span
              style={{ width: "13%" }}
              className="table-td"
              translate="label_withdrawals"
            >
              Bet amount
            </span>
            <span
              style={{ width: "13%" }}
              className="table-td "
              translate="label_expenses"
            >
              Profit and loss
            </span>{" "}
            {filter?.tab != "Casino" && (
              <span
                style={{ width: "13%" }}
                className="table-td "
                translate="label_expenses"
              >
                Action
              </span>
            )}
          </div>
        </div>
        <div className="table-tbody">
          {" "}
          {data?.length > 0 ? (
            data?.map((res) => {
              return (
                <>
                  <div className="table-tr">
                    <div
                      onClick={() => setShow(res)}
                      style={{ width: "13%" }}
                      className="table-td"
                    >
                      {moment(res?.eventDateTime || res?.timeInserted).format(
                        "YYYY-MM-DD hh:mm:ss"
                      )}
                    </div>{" "}
                    <div style={{ width: "32%" }} className="table-td">
                      {res?.eventName || res?.casinoName} |{" "}
                      {obj[res?.eventType] || res?.platform} |{" "}
                      {filter?.tab == "Casino"
                        ? upperCase(res?.casinoType)
                        : filter?.betType == "BetFair"
                        ? res?.market
                        : startCase(filter?.betType)}
                    </div>{" "}
                    <div style={{ width: "13%" }} className="table-td">
                      {res?.total?.totalBet || res?.betAmount}
                    </div>{" "}
                    <div
                      style={{
                        width: "13%",
                        color:
                          (filter?.tab == "Casino"
                            ? toFixedWithoutRounding(res?.playerPL)
                            : res?.total?.pl) > 0
                            ? "green"
                            : "red",
                      }}
                      className="table-td"
                    >
                      {filter?.tab == "Casino"
                        ? toFixedWithoutRounding(res?.playerPL)
                        : Math.abs(res?.total?.pl)}
                    </div>{" "}
                    {filter?.tab != "Casino" && (
                      <div style={{ width: "13%" }} className="table-td">
                        {res?._id == show?._id ? (
                          <FaMinus
                            style={{ width: "12px" }}
                            onClick={() => setShow({})}
                          />
                        ) : (
                          <FaPlus
                            style={{ width: "12px" }}
                            onClick={() => setShow(res)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                  {res?._id == show?._id && (
                    <div
                      style={{ height: "auto" }}
                      className="table-data data-col-6 RNG js_table"
                    >
                      <div className="table-thead">
                        <div className="table-tr">
                          <div
                            style={{
                              width: filter?.tab == "Casino" ? "18%" : "8%",
                            }}
                            className="table-td"
                            translate="label_bet_time"
                          >
                            Bet Id
                          </div>{" "}
                          <div
                            style={{ width: "13%" }}
                            className="table-td"
                            translate="label_bet_time"
                          >
                            Bet Time
                          </div>
                          <div
                            style={{ width: "32%" }}
                            className="table-td"
                            translate="label_bet_money"
                          >
                            Event Name
                          </div>
                          <div
                            style={{ width: "20%" }}
                            className="table-td"
                            translate="label_valid_bet"
                          >
                            Team Name
                          </div>
                          <div
                            style={{ width: "8%" }}
                            className="table-td"
                            translate="label_valid_bet"
                          >
                            Bet Amount
                          </div>
                          {filter?.tab != "Casino" && (
                            <div
                              style={{ width: "8%" }}
                              className="table-td"
                              translate="label_awarded"
                            >
                              Odds
                            </div>
                          )}
                          <div
                            style={{ width: "8%" }}
                            className="table-td"
                            translate="label_profit_loss"
                          >
                            Profit and loss
                          </div>
                        </div>
                      </div>
                      <div style={{ height: "100%" }} className="table-tbody">
                        {" "}
                        {show?.bets_list?.map((res) => {
                          return (
                            <div className="table-tr">
                              <div
                                style={{
                                  width: filter?.tab == "Casino" ? "18%" : "8%",
                                }}
                                className="table-td"
                              >
                                {res?.matchBetId ||
                                  res?.casinoBetId ||
                                  res?.clientName}
                              </div>{" "}
                              <div
                                style={{ width: "13%" }}
                                className="table-td"
                              >
                                {moment(res?.timeInserted).format(
                                  "YYYY-MM-DD hh:mm:ss"
                                )}
                              </div>{" "}
                              <div
                                style={{ width: "32%" }}
                                className="table-td"
                              >
                                {res?.matchName || res?.casinoName} |{" "}
                                {obj[res?.eventType] || res?.platform} |{" "}
                                {filter?.tab == "Casino"
                                  ? upperCase(res?.casinoType)
                                  : filter?.betType == "BetFair"
                                  ? "Match Odds"
                                  : startCase(filter?.betType)}
                              </div>{" "}
                              <div
                                style={{ width: "20%" }}
                                className="table-td"
                              >
                                {res?.teamName ||
                                  res?.fancyName ||
                                  res?.casinoName}{" "}
                              </div>{" "}
                              <div style={{ width: "8%" }} className="table-td">
                                {res?.amount || res?.betAmount}
                              </div>{" "}
                              {filter?.tab != "Casino" && (
                                <div
                                  style={{ width: "8%" }}
                                  className="table-td"
                                >
                                  {filter?.betType == "Fancy"
                                    ? `${res?.bhav} / ${res?.betRun}`
                                    : res?.bhav}
                                </div>
                              )}
                              <div
                                style={{
                                  width: "8%",
                                  color:
                                    (filter?.tab == "Casino"
                                      ? toFixedWithoutRounding(res?.playerPL)
                                      : ShowPrice(res)) > 0
                                      ? "green"
                                      : "red",
                                }}
                                className="table-td"
                              >
                                {" "}
                                {filter?.tab == "Casino"
                                  ? toFixedWithoutRounding(res?.playerPL)
                                  : Math.abs(ShowPrice(res))}
                              </div>{" "}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              );
            })
          ) : (
            <div
              style={{
                "-webkit-text-align": "center",
                "text-align": "center",
                "padding-top": "190px",
              }}
            >
              No matched data found
            </div>
          )}
        </div>
        <div className="table-footer">
          <div className="table-tr all_footer">
            <div className="table-td" translate="label_total">
              Total
            </div>
            <div className="table-td js_deposit" data-amount>
              0
            </div>
            <div className="table-td js_withdraw" data-amount>
              0
            </div>
            <div className="table-td js_betAmount" data-amount>
              0
            </div>
            <div className="table-td js_winAmount" data-amount>
              0
            </div>
            <div className="table-td js_rebate" data-amount>
              0
            </div>
            <div className="table-td js_promotion" data-amount>
              0
            </div>
            <div className="table-td js_invite hide" data-amount>
              0
            </div>
            <div className="table-td js_profitAndLoss" data-amount>
              0
            </div>
          </div>
          <div className="table-tr valid_footer" style={{ display: "none" }}>
            <div className="table-td" translate="label_total">
              Total
            </div>
            <div className="table-td js_deposit" data-amount>
              0.00
            </div>
            <div className="table-td js_withdraw" data-amount>
              0.00
            </div>
            <div className="table-td js_betAmount" data-amount>
              0.00
            </div>
            <div className="table-td js_validBetAmount" data-amount>
              0.00
            </div>
            <div className="table-td js_winAmount" data-amount>
              0.00
            </div>
            <div className="table-td js_rebate" data-amount>
              0.00
            </div>
            <div className="table-td js_promotion" data-amount>
              0.00
            </div>
            <div className="table-td js_invite" data-amount>
              0.00
            </div>
            <div className="table-td js_profitAndLoss" data-amount>
              0.00
            </div>
          </div>
        </div>
        <div className="table-page">
          <div className="pagination-wrap js_pagination" />

          <div className="timezone-tips">
            Reminder: The data above is based on GMT + 8
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfitLoss;
